import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useNavigate } from 'react-router';

import {
  FileModel,
  MediaTypesEnum,
  bytesToKBytes,
  bytesToMBytes,
} from 'dash-common';
import { FileTypeIcon, Flex, Icon, SortHeader, useScreenResolution, Text, TableWitPagination, SortContext, formatDisplayDate } from 'dash-ui';

import { MediaFragment } from 'gql/generated.graphql';
import { RoutesPath } from 'flow-components/routes/routes';

const MediaFilesTable = ({
  media,
  setFileToPreviewId,
  propertyId = '',
}: {
  media: { files: MediaFragment[]; folders: MediaFragment[] };
  setFileToPreviewId: (id: string) => void;
  propertyId?: string;
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state: sort } = useContext(SortContext);
  const {
    devices: { isMobile },
  } = useScreenResolution();

  const tableData = useMemo(() => {
    if (sort?.fieldName === 'sizeInBytes') {
      return [...media.folders, ...media.files].sort((a, b) => {
        const aSize = a.sizeInBytes;
        const bSize = b.sizeInBytes;
        if (aSize < bSize) return sort.sortOrder === 1 ? -1 : 1;
        if (aSize > bSize) return sort.sortOrder === 1 ? 1 : -1;
        return 0;
      });
    }
    return [...media.folders, ...media.files];
  }, [media, sort]);

  const columns = useMemo(() => {
    return [
      {
        Header: () => (
          <SortHeader accessor="name">
            {isMobile ? t('common:labels:file') : t('common:labels:fileName')}
          </SortHeader>
        ),
        Cell: ({
          value,
          row,
        }: {
          value: FileModel;
          row: { original: any; index: number };
        }) => {
          const { folderName, type, _id: mediaId } = row.original;
          const isFolder = type === MediaTypesEnum.FOLDER;

          return (
            <Flex
              alignItems="center"
              onClick={() => {
                if (isFolder) {
                  return navigate(
                    RoutesPath.PropertyMediaFolder.replace(
                      ':id',
                      propertyId
                    ).replace(':folderId', mediaId)
                  );
                }
                setFileToPreviewId(mediaId);
              }}
            >
              {isFolder || !value ? (
                <Icon color={theme.colors.n60} name="folder" size={20} mr={2} />
              ) : (
                <FileTypeIcon mr={2} sm file={value} />
              )}
              <Text ellipsis>{folderName || value?.name}</Text>
            </Flex>
          );
        },
        accessor: 'files.0',
        ...(isMobile ? { width: 190 } : {}),
      },
      {
        Header: () => (
          <SortHeader accessor="sizeInBytes">
            {t('common:labels:size')}
          </SortHeader>
        ),
        accessor: 'sizeInBytes',
        Cell: ({ value }: { value: number }) =>
          bytesToMBytes(value) > 1
            ? t('common:labels.mBytes', {
                size: bytesToMBytes(value),
              })
            : t('common:labels.kBytes', {
                size: bytesToKBytes(value),
              }),
        ...(isMobile ? { width: 82 } : {}),
      },
      {
        Header: () => (
          <SortHeader accessor="createdAt">
            {t('common:labels:added')}
          </SortHeader>
        ),
        accessor: 'createdAt',
        Cell: ({ value }: { value: Date }) => formatDisplayDate(value),
        ...(isMobile ? { width: 105 } : {}),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, isMobile, theme]);

  return (
    <TableWitPagination
      w="100%"
      tableProps={{
        data: tableData,
        columns,
      }}
      paginationProps={{
        previousButtonLabel: t('common:labels.previous'),
        nextButtonLabel: t('common:labels.next'),
        label: t('pages:propertyPage.paginationMediaLabel'),
      }}
    />
  );
};

export default MediaFilesTable;
